import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { useWeather, WeatherBadge } from '../WeatherBadge';
import { getTeamStatsUrl } from '../../utils/teamLinks';
import { TeamStatsModal } from '../TeamStatsModal';

const CalendarView = ({ games, ticketData, isAuthenticated, hidePlayedGames, onToggleHidePlayed, totalSeats = 6, totalParking = 2, partnerOptions = [], isMobileView = false, currentEvent = null }) => {
  // State to show/hide past months
  const [showPastMonths, setShowPastMonths] = useState(false);
  
  // Team stats modal
  const [teamStatsModal, setTeamStatsModal] = useState(null);
  
  // Weather forecast data
  const weatherForecast = useWeather();
  
  // Check if this is a family/personal event (non-sports)
  const isFamilyEvent = currentEvent?.sport?.toLowerCase() === 'family';
  // Check if this is a concerts/theater event
  const isConcertsEvent = currentEvent?.sport?.toLowerCase() === 'concerts';
  // Check if this is any non-sports event
  const isNonSportsEvent = isFamilyEvent || isConcertsEvent;
  
  // Helper to parse date as local date (not UTC)
  const parseLocalDate = (dateString) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Get current month key for filtering
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentMonthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  // Group all games by month first
  const allGamesByMonth = games.reduce((acc, game) => {
    const date = parseLocalDate(game.date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(game);
    return acc;
  }, {});

  const allSortedMonths = Object.keys(allGamesByMonth).sort();
  
  // Filter to show only current and future months by default
  const sortedMonths = showPastMonths 
    ? allSortedMonths 
    : allSortedMonths.filter(m => m >= currentMonthKey);
  
  // Use filtered months for display
  const gamesByMonth = sortedMonths.reduce((acc, monthKey) => {
    acc[monthKey] = allGamesByMonth[monthKey];
    return acc;
  }, {});

  // Count past months for button label
  const pastMonthsCount = allSortedMonths.filter(m => m < currentMonthKey).length;

  const getMonthName = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Get team logo based on sport type
  const getTeamLogo = () => {
    const sport = currentEvent?.sport?.toLowerCase();
    if (sport === 'hockey') return '/dallas-stars-logo.svg';
    if (sport === 'football') return '/dallas-cowboys-logo.png';
    if (sport === 'basketball') return '/dallas-mavs-logo.png';
    if (sport === 'baseball') return '/texas-rangers-logo.png';
    return '/favicon-ticketasaurus.svg';
  };

  const teamLogo = getTeamLogo();

  // Mobile view - simple list by month
  if (isMobileView) {
    return (
      <>
      <div className="space-y-4">
        {/* Legend - Compact for mobile */}
        <div className="flex items-center justify-between py-2 border-b border-border">
          <div className="flex gap-2 text-xs flex-wrap">
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-blue-500 rounded"></div>Upcoming</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-green-500 rounded"></div>Win</span>
            <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded"></div>Loss</span>
          </div>
          {pastMonthsCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPastMonths(!showPastMonths)}
              className="h-7 text-xs px-2"
            >
              {showPastMonths ? 'Hide Past' : `Past (${pastMonthsCount})`}
            </Button>
          )}
        </div>

        {sortedMonths.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No upcoming games</div>
        ) : sortedMonths.map((monthKey) => {
          const gamesThisMonth = gamesByMonth[monthKey];
          
          return (
            <div key={monthKey} className="border border-border rounded-lg overflow-hidden">
              <h3 className="text-base font-bold p-2 bg-muted/50 text-center">{getMonthName(monthKey)}</h3>
              
              <div className="divide-y divide-border">
                {gamesThisMonth.map((game, idx) => {
                  const gameData = ticketData[String(game.game_index)] || ticketData[game.game_index] || {};
                  const hasScore = !isNonSportsEvent && (gameData.mavs_score > 0 || gameData.opponent_score > 0);
                  const isWin = hasScore && gameData.mavs_score > gameData.opponent_score;
                  const isPlayoff = game.season_type === 'playoff' || game.type === 'playoff';
                  const date = parseLocalDate(game.date);
                  
                  // Count assigned tickets - ensure array is correct length
                  const rawTickets = gameData.tickets || [];
                  const tickets = rawTickets.length > 0 ? rawTickets.slice(0, totalSeats) : Array(totalSeats).fill('');
                  // Pad to totalSeats if needed
                  while (tickets.length < totalSeats) {
                    tickets.push('');
                  }
                  const parking = gameData.parking || [];
                  const assignedTickets = tickets.filter(t => t && t !== '').length;
                  const assignedParking = parking.filter(p => p && p !== '').length;
                  
                  return (
                    <div
                      key={idx}
                      className={`px-2 py-1 flex items-center gap-2 ${
                        isFamilyEvent
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : isConcertsEvent
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : hasScore
                          ? isWin 
                            ? 'bg-green-100 dark:bg-green-900/50'
                            : 'bg-red-100 dark:bg-red-900/50'
                          : isPlayoff
                          ? 'bg-amber-100 dark:bg-amber-900/50'
                          : 'bg-white dark:bg-gray-800'
                      }`}
                    >
                      {/* Left: Date - Compact */}
                      <div className="w-12 text-center flex-shrink-0">
                        <div className="text-[10px] font-medium text-gray-500 uppercase leading-tight">{date.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white leading-tight">{date.getDate()}</div>
                        <div className="text-[10px] text-gray-500 leading-tight">{date.toLocaleDateString('en-US', { month: 'short' })}</div>
                      </div>
                      
                      {/* Team Logo / Event Icon - Small icon */}
                      {isFamilyEvent ? (
                        <span className="w-5 h-5 flex items-center justify-center text-base flex-shrink-0">🕐</span>
                      ) : isConcertsEvent ? (
                        <span className="w-5 h-5 flex items-center justify-center text-base flex-shrink-0">🎭</span>
                      ) : (
                        <img src={teamLogo} alt="" className="w-5 h-5 object-contain flex-shrink-0" />
                      )}
                      
                      {/* Center: Game info + Tickets - centered */}
                      <div className="flex-1 text-center">
                        {/* Line 1: Opponent/Event + Score */}
                        <div className="flex items-center justify-center gap-1">
                          {isNonSportsEvent ? (
                            <span className="text-xs font-semibold text-gray-900 dark:text-white">
                              {game.opponent}
                            </span>
                          ) : (() => {
                            const statsUrl = getTeamStatsUrl(game.opponent, currentEvent?.sport);
                            return statsUrl ? (
                              <button
                                className="text-xs font-semibold text-sky-400 underline underline-offset-2 decoration-sky-400/60 hover:text-sky-300"
                                onClick={(e) => { e.stopPropagation(); setTeamStatsModal({ teamName: game.opponent, sport: currentEvent?.sport, espnUrl: statsUrl }); }}
                                data-testid="team-stats-link"
                              >
                                vs {game.opponent}
                              </button>
                            ) : (
                              <span className="text-xs font-semibold text-gray-900 dark:text-white">
                                vs {game.opponent}
                              </span>
                            );
                          })()}
                          {hasScore && (
                            <span className={`text-xs font-bold ${isWin ? 'text-green-600' : 'text-red-600'}`}>
                              {isWin ? 'W' : 'L'} {gameData.mavs_score}-{gameData.opponent_score}
                            </span>
                          )}
                        </div>
                        {/* Show end date for multi-day events */}
                        {game.end_date && (
                          <div className="text-[10px] text-teal-600 dark:text-teal-400 font-medium">
                            🏖️ thru {new Date(game.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        )}
                        {!hasScore && !game.end_date && (
                          <div className="text-[10px] text-gray-500">{game.timeCT}</div>
                        )}
                        
                        {/* Line 2: Ticket allocations - centered, or notify contacts for family events */}
                        {isAuthenticated && !isFamilyEvent && (
                          <div className="flex items-center justify-center gap-0.5 mt-0.5">
                            {tickets.length > 0 ? (
                              tickets.map((t, i) => {
                                const partnerColors = {
                                  'F': 'bg-blue-500',
                                  'B': 'bg-orange-500', 
                                  'Y': 'bg-green-500',
                                  'G': 'bg-purple-500',
                                  'R': 'bg-red-500'
                                };
                                const bgColor = t ? (partnerColors[t] || 'bg-gray-500') : 'bg-gray-200 dark:bg-gray-700';
                                return (
                                  <span 
                                    key={i} 
                                    className={`w-4 h-4 rounded text-center text-[10px] font-bold leading-4 ${bgColor} ${t ? 'text-white' : 'text-gray-400'}`}
                                  >
                                    {t || '·'}
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        )}
                        {isAuthenticated && isFamilyEvent && (gameData.notify_contacts || []).length > 0 && (
                          <div className="flex items-center justify-center gap-0.5 mt-0.5 flex-wrap">
                            {(gameData.notify_contacts || []).map((nc) => (
                              <span
                                key={nc.contact_id}
                                className="inline-flex items-center px-1 py-0 rounded-full text-[8px] font-semibold bg-blue-600 text-white"
                              >
                                {nc.name.split(' ')[0]}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {/* Weather badge */}
                      <WeatherBadge dateStr={game.date} forecast={weatherForecast} compact={true} />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Team Stats Modal */}
      {teamStatsModal && (
        <TeamStatsModal
          teamName={teamStatsModal.teamName}
          sport={teamStatsModal.sport}
          espnUrl={teamStatsModal.espnUrl}
          onClose={() => setTeamStatsModal(null)}
        />
      )}
      </>
    );
  }

  // Desktop view - original calendar grid
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  return (
    <div className="space-y-6">
      {/* Legend and Filter Button - All on one line */}
      <div className="flex items-center justify-between py-2 border-b border-border">
        <div className="flex gap-6 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-blue-500 rounded"></div>
            <span className="font-medium">Upcoming</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="font-medium">Win</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="font-medium">Loss</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-amber-500 rounded"></div>
            <span className="font-medium">Playoff</span>
          </div>
          <div className="flex items-center gap-2" data-testid="legend-weather-actual">
            <div className="w-4 h-4 rounded border border-emerald-500/60 bg-emerald-500/15"></div>
            <span className="font-medium">Actual Weather</span>
          </div>
          <div className="flex items-center gap-2" data-testid="legend-weather-forecast">
            <div className="w-4 h-4 rounded border border-dashed border-amber-400/70"></div>
            <span className="font-medium italic">Forecast</span>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={onToggleHidePlayed}
            className="h-9"
            data-testid="calendar-toggle-hide-past"
            title="Hide past-month rows within the current month"
          >
            {hidePlayedGames ? '👁️ Show Past Events' : '🙈 Hide Past Events'}
          </Button>
          {pastMonthsCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPastMonths(!showPastMonths)}
              className="h-9"
            >
              {showPastMonths ? 'Hide Past Months' : `View Past (${pastMonthsCount} months)`}
            </Button>
          )}
        </div>
      </div>

      {sortedMonths.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border border-border rounded-lg">
          No upcoming games found
        </div>
      ) : sortedMonths.map((monthKey) => {
        const [year, month] = monthKey.split('-');
        const monthNum = parseInt(month) - 1;
        const daysInMonth = getDaysInMonth(parseInt(year), monthNum);
        const firstDay = getFirstDayOfMonth(parseInt(year), monthNum);
        const gamesThisMonth = gamesByMonth[monthKey];

        // Create a map of games by day, expanding multi-day events to all days they span
        const gamesByDay = gamesThisMonth.reduce((acc, game) => {
          const startDate = parseLocalDate(game.date);
          const startDay = startDate.getDate();
          const startMonth = startDate.getMonth();
          const startYear = startDate.getFullYear();
          
          // Check if this is a multi-day event
          if (game.end_date) {
            const endDate = parseLocalDate(game.end_date);
            const endDay = endDate.getDate();
            const endMonth = endDate.getMonth();
            const endYear = endDate.getFullYear();
            
            // Add event to each day it spans (within this month)
            let currentDate = new Date(startDate);
            while (currentDate <= endDate) {
              // Only add if current date is in this month
              if (currentDate.getMonth() === parseInt(month) - 1 && currentDate.getFullYear() === parseInt(year)) {
                const currentDay = currentDate.getDate();
                if (!acc[currentDay]) acc[currentDay] = [];
                // Mark if this is start, middle, or end of multi-day event
                const isStart = currentDate.getTime() === startDate.getTime();
                const isEnd = currentDate.getTime() === endDate.getTime();
                acc[currentDay].push({
                  ...game,
                  _multiDay: true,
                  _isStart: isStart,
                  _isEnd: isEnd,
                  _isContinuation: !isStart
                });
              }
              currentDate.setDate(currentDate.getDate() + 1);
            }
          } else {
            // Single day event
            if (!acc[startDay]) acc[startDay] = [];
            acc[startDay].push(game);
          }
          return acc;
        }, {});

        // Skip leading empty weeks when Hide Past Events is on (only for the CURRENT month)
        const todayForCal = new Date();
        const todayStrCal = `${todayForCal.getFullYear()}-${String(todayForCal.getMonth() + 1).padStart(2, '0')}-${String(todayForCal.getDate()).padStart(2, '0')}`;
        let leadingSkipCells = 0;
        if (hidePlayedGames && !showPastMonths) {
          const totalCells = firstDay + daysInMonth;
          const totalWeeks = Math.ceil(totalCells / 7);
          const monthDateStrLocal = (d) => `${year}-${String(monthNum + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
          let firstVisibleWeek = 0;
          outer: for (let w = 0; w < totalWeeks; w++) {
            for (let c = 0; c < 7; c++) {
              const cellIdx = w * 7 + c;
              const dayNum = cellIdx - firstDay + 1;
              if (dayNum < 1 || dayNum > daysInMonth) continue;
              const dstr = monthDateStrLocal(dayNum);
              if (dstr >= todayStrCal) { firstVisibleWeek = w; break outer; }
              if ((gamesByDay[dayNum] || []).length > 0) { firstVisibleWeek = w; break outer; }
            }
          }
          leadingSkipCells = firstVisibleWeek * 7;
        }

        return (
          <div key={monthKey} className="border border-border rounded-lg p-4">
            <h3 className="text-2xl font-bold mb-4 text-center">{getMonthName(monthKey)}</h3>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-2">
              {/* Day headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center font-bold text-sm p-2 text-muted-foreground border-b border-border">
                  {day}
                </div>
              ))}
              
              {/* Empty cells for days before month starts (skipped if leading weeks hidden) */}
              {leadingSkipCells < firstDay && Array.from({ length: firstDay - leadingSkipCells }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[140px] p-1 bg-muted/20"></div>
              ))}
              
              {/* Days of month */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                // Skip days that fall in the hidden leading weeks
                if ((firstDay + i) < leadingSkipCells) return null;
                const gamesOnDay = gamesByDay[day] || [];
                const hasGames = gamesOnDay.length > 0;

                return (
                  <div
                    key={day}
                    className={`min-h-[140px] p-2 border border-border rounded-lg ${
                      hasGames ? 'bg-primary/5' : 'bg-background'
                    }`}
                  >
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-base font-bold text-muted-foreground">{day}</span>
                        {(() => {
                          const dateKey = `${year}-${String(monthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                          return weatherForecast[dateKey] ? (
                            <WeatherBadge dateStr={dateKey} forecast={weatherForecast} compact={true} />
                          ) : null;
                        })()}
                      </div>
                      {hasGames && (
                        <div className="flex-1 space-y-2">
                          {gamesOnDay.map((game, idx) => {
                            const gameData = ticketData[String(game.game_index)] || ticketData[game.game_index] || {};
                            const hasScore = gameData.mavs_score > 0 || gameData.opponent_score > 0;
                            const isPlayoff = game.season_type === 'playoff' || game.type === 'playoff';
                            const isWin = hasScore && gameData.mavs_score > gameData.opponent_score;
                            
                            // Get tickets and parking arrays
                            const tickets = gameData.tickets || [];
                            const parking = gameData.parking || [];
                            
                            // Dynamic color classes for partners
                            const getPartnerColor = (value) => {
                              if (!value) return 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500';
                              const colorClasses = [
                                'bg-blue-600 text-white',
                                'bg-orange-500 text-white',
                                'bg-green-600 text-white',
                                'bg-purple-600 text-white',
                                'bg-pink-600 text-white',
                              ];
                              const index = partnerOptions.findIndex(p => p.id === value);
                              return index >= 0 ? colorClasses[index % colorClasses.length] : 'bg-gray-500 text-white';
                            };
                            
                            // Check if this is a multi-day event
                            const isMultiDay = game._multiDay;
                            const isMultiDayStart = game._isStart;
                            const isMultiDayEnd = game._isEnd;
                            const isMultiDayContinuation = game._isContinuation;
                            
                            // Multi-day events get special styling
                            if (isMultiDay) {
                              return (
                                <div
                                  key={idx}
                                  className={`p-2 shadow-sm flex flex-col relative ${
                                    isMultiDayStart ? 'rounded-l-lg rounded-r-none' : 
                                    isMultiDayEnd ? 'rounded-r-lg rounded-l-none' : 
                                    'rounded-none'
                                  } ${
                                    isMultiDayContinuation 
                                      ? 'bg-teal-200 dark:bg-teal-800 border-l-0' 
                                      : 'bg-teal-100 dark:bg-teal-900 border-l-4 border-teal-600'
                                  }`}
                                  style={{ minHeight: '60px' }}
                                >
                                  {/* Only show name on first day or continuation marker */}
                                  {isMultiDayStart ? (
                                    <>
                                      <div className="text-xs font-bold text-teal-800 dark:text-teal-200 line-clamp-2">
                                        🏖️ {game.opponent}
                                      </div>
                                      {game.timeCT && (
                                        <div className="text-[10px] text-teal-600 dark:text-teal-300 mt-0.5">
                                          {game.timeCT}
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="text-[10px] text-teal-600 dark:text-teal-300 italic">
                                      {isMultiDayEnd ? `📍 ${game.opponent} ends` : '↔️ continues'}
                                    </div>
                                  )}
                                </div>
                              );
                            }
                            
                            return (
                              <div
                                key={idx}
                                className={`p-3 rounded-lg border-l-4 shadow-sm h-[160px] flex flex-col relative ${
                                  isFamilyEvent
                                    ? 'bg-blue-50 border-blue-500 dark:bg-blue-950 dark:border-blue-400'
                                    : isConcertsEvent
                                    ? 'bg-purple-50 border-purple-500 dark:bg-purple-950 dark:border-purple-400'
                                    : hasScore
                                    ? isWin 
                                      ? 'bg-green-50 border-green-600 dark:bg-green-950 dark:border-green-400'
                                      : 'bg-red-50 border-red-600 dark:bg-red-950 dark:border-red-400'
                                    : isPlayoff
                                    ? 'bg-amber-50 border-amber-600 dark:bg-amber-950 dark:border-amber-400'
                                    : 'bg-blue-50 border-blue-600 dark:bg-blue-950 dark:border-blue-400'
                                }`}
                              >
                                {/* Team Logo / Event Icon - Top right corner */}
                                {isFamilyEvent ? (
                                  <span className="absolute top-2 right-2 text-base opacity-70">🕐</span>
                                ) : isConcertsEvent ? (
                                  <span className="absolute top-2 right-2 text-base opacity-70">🎭</span>
                                ) : (
                                  <img 
                                    src={teamLogo} 
                                    alt="" 
                                    className="absolute top-2 right-2 w-5 h-5 object-contain opacity-70" 
                                  />
                                )}
                                
                                {/* Opponent/Event Name - Fixed 2-line height */}
                                <div className="text-sm font-bold text-gray-900 dark:text-gray-100 h-[40px] leading-tight flex items-start pr-6">
                                  <span className="line-clamp-2">{isNonSportsEvent ? game.opponent : `vs ${game.opponent}`}</span>
                                </div>
                                
                                {/* Score or Time - Prominent */}
                                {hasScore ? (
                                  <div className="text-base font-bold mb-2">
                                    {isWin ? (
                                      <span className="text-green-700 dark:text-green-300">
                                        W {gameData.mavs_score}-{gameData.opponent_score}
                                      </span>
                                    ) : (
                                      <span className="text-red-700 dark:text-red-300">
                                        L {gameData.mavs_score}-{gameData.opponent_score}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    {game.timeCT}
                                  </div>
                                )}
                                
                                {/* Ticket Allocation or Notify Contacts */}
                                {isAuthenticated && (
                                  <div className="space-y-1 border-t border-gray-300 dark:border-gray-500 pt-2 mt-auto">
                                    {isFamilyEvent ? (
                                      /* Notify contacts for family events */
                                      <div className="flex gap-0.5 justify-center flex-wrap">
                                        {(gameData.notify_contacts || []).length > 0 ? (
                                          (gameData.notify_contacts || []).map((nc) => (
                                            <span
                                              key={nc.contact_id}
                                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-blue-600 text-white"
                                              title={nc.name}
                                            >
                                              {nc.name.split(' ')[0]}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-[10px] text-gray-400 italic">No contacts</span>
                                        )}
                                      </div>
                                    ) : (
                                      /* Tickets - dynamic based on settings */
                                      <div className="flex gap-0.5 justify-center flex-wrap">
                                        {(tickets.length ? tickets : Array(totalSeats).fill('')).slice(0, totalSeats).map((t, i) => (
                                          <div
                                            key={i}
                                            className={`w-5 h-5 text-xs font-bold flex items-center justify-center rounded ${getPartnerColor(t)}`}
                                          >
                                            {t || '-'}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                    {/* Notes */}
                                    {gameData.notes && (
                                      <div className="text-xs text-gray-600 dark:text-gray-300 truncate text-center italic">
                                        {gameData.notes}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export { CalendarView };
export default CalendarView;
